import { forexBasicsQuote } from '@/mocks/forexBasics';
import SectionHeading from '@/components/base/SectionHeading';
import ScrollReveal from '@/components/base/ScrollReveal';

const toneClasses: Record<string, string> = {
  accent: 'bg-accent-100 text-accent-700',
  primary: 'bg-primary-100 text-primary-700',
  secondary: 'bg-secondary-100 text-secondary-700',
};

export default function PriceExample() {
  const { pair, bid, ask, spreadPips, note, definitions } = forexBasicsQuote;

  return (
    <section className="border-y border-background-200 bg-background-100/60">
      <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20 lg:px-10">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
          <div>
            <SectionHeading
              eyebrow="ตัวอย่างราคา"
              title="Bid, Ask และ Spread คืออะไร"
              subtitle="ทำความเข้าใจราคาในตลาด Forex ผ่านตัวอย่างคู่เงิน EUR/USD เพื่อให้เห็นว่าต้นทุนของออเดอร์เกิดขึ้นได้อย่างไร"
            />

            <div className="mt-10 flex flex-col gap-4">
              {definitions.map((item, index) => (
                <ScrollReveal key={item.name} delay={index * 80}>
                  <div className="flex items-start gap-4 rounded-xl border border-background-200 bg-background-50 p-5">
                    <span
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
                        toneClasses[item.tone] ?? toneClasses.primary
                      }`}
                    >
                      <i className={`${item.icon} text-lg`} />
                    </span>
                    <div>
                      <h3 className="font-heading text-base font-semibold text-foreground-950">
                        {item.name}
                        <span className="ml-2 text-sm font-normal text-foreground-500">{item.thai}</span>
                      </h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>

          <ScrollReveal delay={120}>
            <div className="flex h-full flex-col rounded-2xl border border-background-200 bg-background-50 p-6 md:p-7">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-num text-base font-semibold text-foreground-950">{pair}</span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-2.5 py-1 text-[11px] font-medium text-accent-800">
                  <i className="ri-flask-line text-sm" />
                  ราคาตัวอย่างเพื่อการสอน
                </span>
              </div>

              <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-xl border border-accent-200 bg-accent-50 p-4">
                  <span className="flex items-center gap-1.5 text-xs font-semibold text-accent-800">
                    <i className="ri-arrow-down-line text-sm" />
                    Bid · ขาย (Sell)
                  </span>
                  <p className="mt-2 font-num text-2xl font-semibold text-foreground-950">{bid}</p>
                  <p className="mt-1 text-xs text-foreground-600">ราคาที่เราได้เมื่อขาย</p>
                </div>
                <div className="rounded-xl border border-primary-200 bg-primary-50 p-4">
                  <span className="flex items-center gap-1.5 text-xs font-semibold text-primary-800">
                    <i className="ri-arrow-up-line text-sm" />
                    Ask · ซื้อ (Buy)
                  </span>
                  <p className="mt-2 font-num text-2xl font-semibold text-foreground-950">{ask}</p>
                  <p className="mt-1 text-xs text-foreground-600">ราคาที่เราจ่ายเมื่อซื้อ</p>
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between rounded-xl border border-secondary-200 bg-secondary-100/60 px-4 py-3.5">
                <span className="flex items-center gap-2 text-sm font-medium text-secondary-900">
                  <i className="ri-arrow-left-right-line text-base" />
                  Spread (ส่วนต่าง Ask − Bid)
                </span>
                <span className="font-num text-base font-semibold text-secondary-900">{spreadPips} pips</span>
              </div>

              <div className="relative mt-6 flex-1 rounded-xl border border-background-200 bg-background-100/50 p-5">
                <p className="text-xs font-medium text-foreground-600">ตำแหน่งราคาบนกราฟ (เชิงแนวคิด)</p>
                <div className="mt-5 flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <span className="w-12 text-right text-xs font-medium text-primary-700">Ask</span>
                    <span className="h-1.5 flex-1 rounded-full bg-primary-300" />
                    <span className="font-num w-16 text-right text-xs text-primary-700">{ask}</span>
                  </div>
                  <div className="flex items-center gap-3 pl-14">
                    <span className="flex items-center gap-1.5 rounded-full bg-secondary-100 px-2.5 py-0.5 text-[10px] font-medium text-secondary-900">
                      <i className="ri-drag-move-2-line text-xs" />
                      Spread {spreadPips} pips
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="w-12 text-right text-xs font-medium text-accent-700">Bid</span>
                    <span className="h-1.5 flex-1 rounded-full bg-accent-300" />
                    <span className="font-num w-16 text-right text-xs text-accent-700">{bid}</span>
                  </div>
                </div>
              </div>

              <p className="mt-4 flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
                <i className="ri-information-line mt-0.5 text-sm" />
                {note}
              </p>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}